import React from 'react';
import { Helmet } from 'react-helmet-async';
import { companyInfo } from '@/data/mock';

const defaultKeywords = [
  'civil engineering firm',
  'infrastructure development',
  'structural engineering',
  'transportation engineering',
  'water resources engineering',
  'construction management',
  'Connecticut engineers',
  'Redspire Civil'
];

export const SEOHead = ({
  title,
  description,
  keywords = [],
  image,
  url,
  canonical,
  type = 'website',
  schema
}) => {
  const fullTitle = title ? `${title} | ${companyInfo.name}` : companyInfo.name;
  const metaDescription =
    description ||
    'Redspire Civil Engineering delivers complex infrastructure programs across transportation, water resources, and structural markets.';
  const defaultImage = 'https://images.unsplash.com/photo-1536895058696-a69b1c7ba34f?w=1200&h=630&fit=crop';
  const keywordContent = Array.from(new Set([...defaultKeywords, ...keywords])).filter(Boolean);
  const canonicalUrl =
    canonical ||
    url ||
    (typeof window !== 'undefined' ? window.location.origin + window.location.pathname : companyInfo.website);
  const schemaNodes = Array.isArray(schema) ? schema : schema ? [schema] : [];

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      {keywordContent.length > 0 && <meta name="keywords" content={keywordContent.join(', ')} />}
      <meta name="author" content={companyInfo.name} />
      <meta name="publisher" content={companyInfo.name} />
      <meta name="robots" content="index,follow" />
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={image || defaultImage} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:site_name" content={companyInfo.name} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={image || defaultImage} />

      {/* JSON-LD Schema */}
      {schemaNodes.map((node, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(node)}
        </script>
      ))}
    </Helmet>
  );
};

// Organization Schema
export const getOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": companyInfo.name,
  "url": companyInfo.website,
  "logo": companyInfo.logoUrl,
  "description": "Expert civil engineering services delivering infrastructure solutions in Connecticut and the Northeast.",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "218 Brookside Industrial Park",
    "addressLocality": "Stamford",
    "addressRegion": "CT",
    "postalCode": "06902",
    "addressCountry": "US"
  },
  "telephone": companyInfo.phone,
  "email": companyInfo.email,
  "sameAs": [
    companyInfo.socialLinks.linkedin,
    companyInfo.socialLinks.twitter,
    companyInfo.socialLinks.facebook
  ]
});

// LocalBusiness Schema
export const getLocalBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": companyInfo.website,
  "name": companyInfo.name,
  "image": "https://images.unsplash.com/photo-1536895058696-a69b1c7ba34f?w=1200",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "218 Brookside Industrial Park",
    "addressLocality": "Stamford",
    "addressRegion": "CT",
    "postalCode": "06902",
    "addressCountry": "US"
  },
  "telephone": companyInfo.phone,
  "priceRange": "$$$$",
  "openingHours": "Mo-Fr 08:00-17:00"
});

export const getWebsiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": companyInfo.name,
  "url": companyInfo.website,
  "potentialAction": {
    "@type": "SearchAction",
    "target": `${companyInfo.website}/search?q={search_term_string}`,
    "query-input": "required name=search_term_string"
  }
});

// Service Schema Generator
export const getServiceSchema = (service) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "name": service.title,
  "description": service.shortDescription || service.overview,
  "provider": {
    "@type": "Organization",
    "name": companyInfo.name
  },
  "areaServed": {
    "@type": "State",
    "name": "Connecticut"
  },
  "image": service.heroImage,
  "url": `https://redspire.com/services/${service.id}`,
  "telephone": companyInfo.phone
});

// Breadcrumb Schema Generator
export const getBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url
  }))
});

export default SEOHead;
